import type { StylePackLoader } from '@/themes/stylePack.types';
import { LOADER_STYLES, type LoaderStyle } from '@/themes/loaderStyle';

/**
 * Вид загрузчика своими полями воронки (слово владельца 21.10.2026) — зеркало
 * серверной схемы `backend/src/ai-funnels/block-schema/quiz-loader.ts`.
 * «Загрузчик» — блок `loader_processing` (экран обработки перед результатом).
 *
 * «Перебивает пак» по каждому подполю отдельно: задано — берётся оно; не
 * задано — как в паке (`StylePackLoader`), а без пака — прежний вид
 * (DEFAULT_LOADER_STYLE отрисовки). `ringSize` и `ringStroke` действуют только
 * на вид `ring`. Ручной сейв кабинета идёт мимо схемы сервера, поэтому разбор
 * здесь — зажим отрисовки: негодное = нет поля. Совпадение перечня, границ и
 * кода цвета с сервером держит сторож `quiz-loader.ends.spec.ts`.
 */
/** Вид — перечень пака, не копия (themes/loaderStyle.ts). */
export const QUIZ_LOADER_STYLES = LOADER_STYLES;
/**
 * Место подписи блока относительно индикатора (задание
 * stylepack/loader-title-position): нет поля — над, как всегда.
 */
export const QUIZ_LOADER_TITLE_POSITIONS = ['above', 'below'] as const;
export type QuizLoaderTitlePosition =
  (typeof QUIZ_LOADER_TITLE_POSITIONS)[number];
export const QUIZ_LOADER_BOUNDS = {
  ringSize: { min: 48, max: 240 },
  ringStroke: { min: 1, max: 24 },
} as const;
/** Код цвета — то же правило, что BLOCK_COLOR_PATTERN сервера. */
export const QUIZ_LOADER_COLOR_PATTERN =
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export interface QuizLoaderSettings {
  style?: LoaderStyle;
  showPercent?: boolean;
  /** Цвет заполнения (кольцо или полоса). */
  color?: string;
  /** Цвет дорожки под заполнением. */
  trackColor?: string;
  ringSize?: number;
  ringStroke?: number;
  /** Подпись над (`above`, как без поля) или под (`below`) индикатором. */
  titlePosition?: QuizLoaderTitlePosition;
}

const isColor = (v: unknown): v is string =>
  typeof v === 'string' && QUIZ_LOADER_COLOR_PATTERN.test(v);
const inBounds = (v: unknown, b: { min: number; max: number }): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= b.min && v <= b.max;

/** Разбор сырого `settings.quizLoader`: годное поле остаётся, прочее выброшено. */
export function readQuizLoader(raw: unknown): QuizLoaderSettings {
  const out: QuizLoaderSettings = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.style === 'string' &&
    (QUIZ_LOADER_STYLES as readonly string[]).includes(r.style)
  )
    out.style = r.style as LoaderStyle;
  if (typeof r.showPercent === 'boolean') out.showPercent = r.showPercent;
  if (isColor(r.color)) out.color = r.color;
  if (isColor(r.trackColor)) out.trackColor = r.trackColor;
  if (inBounds(r.ringSize, QUIZ_LOADER_BOUNDS.ringSize))
    out.ringSize = r.ringSize;
  if (inBounds(r.ringStroke, QUIZ_LOADER_BOUNDS.ringStroke))
    out.ringStroke = r.ringStroke;
  if (
    typeof r.titlePosition === 'string' &&
    (QUIZ_LOADER_TITLE_POSITIONS as readonly string[]).includes(r.titlePosition)
  )
    out.titlePosition = r.titlePosition as QuizLoaderTitlePosition;
  return out;
}

export type ResolvedQuizLoader = StylePackLoader & {
  /** Свои поля воронки после зажима — цвета и размеры читаются отсюда. */
  own: QuizLoaderSettings;
};

/**
 * Одна дверь «поле перебивает пак» для загрузчика (публичная воронка и превью).
 *
 * Задание stylepack/loader-block-look: у самого блока бывает свой вид
 * (`block.loaderLook`, те же ключи). Порядок по каждому подполю: поле блока →
 * поле воронки → пак. Оба разбираются той же дверью `readQuizLoader`; нет поля
 * блока — результат тот же, что без третьего аргумента.
 */
export function resolveQuizLoader(
  base: StylePackLoader,
  raw: unknown,
  blockRaw?: unknown,
): ResolvedQuizLoader {
  const own: QuizLoaderSettings = {
    ...readQuizLoader(raw),
    ...readQuizLoader(blockRaw),
  };
  return {
    ...base,
    style: own.style ?? base.style,
    showPercent: own.showPercent ?? base.showPercent,
    own,
  };
}
